/**
 * API Client for QC Agent Backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface Step {
  index: number;
  description: string;
  action_type: string;
  target_element?: any;
  value?: string;
  selenium_code?: string;
  executed: boolean;
  success?: boolean;
  error?: string;
  user_feedback?: string;
}

export interface GenerateStepsRequest {
  test_case: string;
  website_url: string;
  assertions: string[];
}

export interface GenerateStepsResponse {
  session_id: string;
  steps: Step[];
  status: string;
  message: string;
}

export interface StepUpdate {
  step_index: number;
  description?: string;
}

export interface ConfirmStepsRequest {
  session_id: string;
  step_modifications?: StepUpdate[];
}

export interface CodeGenerationResponse {
  session_id: string;
  final_code: string;
  status: string;
  message: string;
}

export interface ExecuteTestRequest {
  session_id: string;
}

export interface ExecutionResponse {
  session_id: string;
  success: boolean;
  error?: string;
  result?: any;
  message: string;
}

export interface SessionResponse {
  session_id: string;
  test_case: string;
  website_url: string;
  assertions: string[];
  steps: Step[];
  final_code?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  async generateSteps(request: GenerateStepsRequest): Promise<GenerateStepsResponse> {
    return this.request<GenerateStepsResponse>('/api/test/generate-steps', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async confirmSteps(request: ConfirmStepsRequest): Promise<CodeGenerationResponse> {
    return this.request<CodeGenerationResponse>('/api/test/confirm-steps', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async regenerateStep(sessionId: string, stepIndex: number): Promise<Step> {
    return this.request<Step>('/api/test/regenerate-step', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        step_index: stepIndex,
      }),
    });
  }

  async executeTest(sessionId: string): Promise<ExecutionResponse> {
    return this.request<ExecutionResponse>('/api/test/execute-full', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
      }),
    });
  }

  async submitFeedback(sessionId: string, stepIndex: number, feedback: string): Promise<any> {
    return this.request('/api/test/feedback', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        step_index: stepIndex,
        feedback: feedback,
      }),
    });
  }

  async getSession(sessionId: string): Promise<SessionResponse> {
    return this.request<SessionResponse>(`/api/test/session/${sessionId}`);
  }

  async getSteps(sessionId: string, includeCode: boolean = false): Promise<Step[]> {
    return this.request<Step[]>(`/api/test/steps/${sessionId}?include_code=${includeCode}`);
  }

  async getCode(sessionId: string): Promise<{ session_id: string; code: string }> {
    return this.request<{ session_id: string; code: string }>(`/api/test/code/${sessionId}`);
  }

  async healthCheck(): Promise<any> {
    return this.request('/api/test/health');
  }
}

export const apiClient = new ApiClient();
